import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Language, Currency, detectLanguage, getCurrency, getTranslations, getStripeCheckoutUrl } from '@/lib/i18n';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

interface LanguageContextType {
  language: Language;
  currency: Currency;
  t: ReturnType<typeof getTranslations>;
  stripeUrl: string;
  setLanguage: (lang: Language) => void;
  isLoadingLanguage: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Use local auth state to avoid circular dependency with AppStateContext
  const [user, setUser] = useState<User | null>(null);
  const [language, setLanguageState] = useState<Language>(() => detectLanguage());
  const [currency, setCurrency] = useState<Currency>(() => getCurrency(detectLanguage()));
  const [isLoadingLanguage, setIsLoadingLanguage] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const userRef = useRef<User | null>(null);

  // Keep ref in sync
  useEffect(() => { userRef.current = user; }, [user]);

  // Listen for auth state changes directly (no useAuth dependency)
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    // Listen for auth changes — IGNORE TOKEN_REFRESHED and duplicate SIGNED_IN
    // to prevent full-app re-render when browser regains focus
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED') return; // silent refresh, no re-render
      // Skip SIGNED_IN if same user already loaded (visibility change re-emit)
      if (event === 'SIGNED_IN' && session?.user?.id && session.user.id === userRef.current?.id) return;
      setUser(session?.user ?? null);
      setProfileLoaded(false); // Reset profile loaded when auth changes
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load language from user profile when authenticated
  useEffect(() => {
    const loadProfileLanguage = async () => {
      if (!user) {
        // No user logged in - use browser detection
        const browserLang = detectLanguage();
        setLanguageState(browserLang);
        setCurrency(getCurrency(browserLang));
        setProfileLoaded(false);
        return;
      }

      // Avoid re-fetching if already loaded for this user
      if (profileLoaded) return;

      setIsLoadingLanguage(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('language')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!error && data?.language) {
          const profileLang = data.language as Language;
          setLanguageState(profileLang);
          setCurrency(getCurrency(profileLang));
          console.log('[Language] Loaded from profile:', profileLang);
        }
        setProfileLoaded(true);
      } catch (error) {
        console.error('Error loading profile language:', error);
      } finally {
        setIsLoadingLanguage(false);
      }
    };

    loadProfileLanguage();
  }, [user, profileLoaded]);

  // Update language in profile when changed (if logged in)
  const setLanguage = useCallback(async (lang: Language) => {
    // Update local state immediately for instant UI feedback
    setLanguageState(lang);
    setCurrency(getCurrency(lang));
    
    console.log('[Language] Setting language to:', lang);

    // Persist to profile if user is logged in
    if (user) {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ language: lang })
          .eq('user_id', user.id);
          
        if (error) {
          console.error('Error saving language preference:', error);
        } else {
          console.log('[Language] Saved to profile:', lang);
        }
      } catch (error) {
        console.error('Error saving language preference:', error);
      }
    }
  }, [user]);

  const t = getTranslations(language);
  const stripeUrl = getStripeCheckoutUrl(language);

  return (
    <LanguageContext.Provider value={{ language, currency, t, stripeUrl, setLanguage, isLoadingLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
